import { mitPositionsauswahl, type AbschlussFall } from '../bestandteile/abschlussLogic';
import type { PlanStandFuerAblage } from './PlanVorschau';

/**
 * Abrechnung des SiGe-Plans: beim PDF-Download und nach dem Senden an den
 * Plotter. Bis Oktober 2026 buchte Plotten nichts — wer den Plan nur
 * plottete und nicht zusätzlich herunterlud, hatte ihn nie abgerechnet.
 *
 * `plot` trägt den geplotteten Stand: eine Buchung legt GENAU dieses Blatt
 * als versendete Fassung ab, statt den inzwischen vielleicht geänderten Plan
 * neu zu erzeugen (plotAbrechnungen.ts). Heruntergeladen wird beim Plot nichts.
 */
export type AbrechnungsZiel =
    | { art: 'download' }
    | { art: 'plot'; stand: PlanStandFuerAblage };

/**
 * Was nach dem Klick (Download) bzw. nach dem gesendeten Auftrag (Plot) kommt:
 *  - 'fassung_fragen': nur ein Paket mit dem Bestandteil „SiGe-Plan" (Plan B5,
 *    Fall 2) — die Frage, ob der Plan als Fassung hinausgeht;
 *  - 'positionsauswahl': der Abrechnungsdialog;
 *  - 'ohne_abrechnung': nichts zu buchen, der Download fragt nach;
 *  - 'nichts': nichts zu buchen beim Plot — das Blatt ist schon unterwegs,
 *    eine Rückfrage hätte keine Wahl mehr.
 */
export type AbrechnungsSchritt = 'fassung_fragen' | 'positionsauswahl' | 'ohne_abrechnung' | 'nichts';

export const abrechnungsSchritt = (a: {
    ziel: AbrechnungsZiel['art'];
    fall: AbschlussFall;
    /** `schrittOhneLeistungsbuchung(…) === 'fassung_fragen'` */
    fassungFragen: boolean;
    /** Die Auswahlliste des Editors (mit Rückfallebene „alle Leistungen") ist nicht leer. */
    positionenAngeboten: boolean;
    /** Der Editor hat einen Schreibweg (`onUpdate`). */
    kannBuchen: boolean;
}): AbrechnungsSchritt => {
    if (a.fall === 'paket' && a.fassungFragen) return 'fassung_fragen';
    if ((mitPositionsauswahl(a.fall) || a.positionenAngeboten) && a.kannBuchen) return 'positionsauswahl';
    return a.ziel === 'plot' ? 'nichts' : 'ohne_abrechnung';
};

/**
 * Was am Ende eines Abrechnungsschritts mit der abgeholten Plot-Abrechnung
 * geschieht (Codex 23.10.2026). `nimmPlotAbrechnung` nimmt sie beim Abholen
 * aus der Warteschlange; bis ihr Schritt endet, hält der Editor sie fest —
 * wird er vorher verlassen, legt er sie zurück, sonst wäre der Plot nie
 * gebucht und nie als Fassung abgelegt.
 *  - 'unberuehrt': ein Download — die festgehaltene Abrechnung gehört nur
 *    zum Plot, ein Download-Schritt fasst sie nicht an;
 *  - 'erledigt': der Plot-Schritt endet im Projekt des Plots, bestätigt oder
 *    bewusst abgelehnt — loslassen, NIE zurücklegen: der Editor holte sie
 *    sofort wieder ab, und ein Fehler im Buchungsweg liefe im Kreis;
 *  - 'zuruecklegen': der Editor zeigt inzwischen ein anderes Projekt — nicht
 *    dorthin buchen, sondern zurück in die Warteschlange; der Editor des
 *    richtigen Projekts holt sie wieder.
 */
export type PlotAbrechnungsEnde = 'unberuehrt' | 'erledigt' | 'zuruecklegen';

export const plotAbrechnungsEnde = (a: {
    ziel: AbrechnungsZiel['art'];
    /** `projektId` der festgehaltenen Plot-Abrechnung; keine festgehalten → null. */
    abgeholtFuer: string | null;
    /** `project.id` des Editors beim Ende des Schritts. */
    offenesProjekt: string;
}): PlotAbrechnungsEnde => {
    if (a.ziel !== 'plot') return 'unberuehrt';
    return a.abgeholtFuer !== null && a.abgeholtFuer !== a.offenesProjekt ? 'zuruecklegen' : 'erledigt';
};

/**
 * Nach einem abgerechneten Plot schließt der Editor (Betreiber 02.11.2026):
 * gedruckt, gebucht, als Fassung abgelegt — im Editor ist nichts mehr zu tun.
 * Aber erst, wenn Ablage und Abschlussschritt durch sind: ein Aushängen davor
 * verwürfe die Paketbuchung (useBestandteilAbschluss, `eingehaengt`) und das
 * Stunden-Pflichtfenster. Hat einer der Schritte etwas zu sagen, bleibt der
 * Editor offen, damit es gelesen wird.
 *  - 'warten': Ablage oder Abschlussschritt laufen, das Stunden-Pflichtfenster
 *    steht offen oder der nächste Abrechnungsschritt dieses Projekts steht an;
 *  - 'schliessen': alles durch, nichts zu melden;
 *  - 'bleiben': eine Meldung (Ablage gescheitert, Warnung, Hinweis des
 *    Abschlussschritts) — nicht mehr von selbst schließen.
 */
export type NachPlotSchliessen = 'warten' | 'schliessen' | 'bleiben';

export const nachPlotSchliessen = (a: {
    ablageLaeuft: boolean;
    ablageGescheitert: boolean;
    /** Ton des Banners im Editor — `null`: keins. */
    hinweisTon: 'fehler' | 'warnung' | 'erfolg' | null;
    abschlussLaeuft: boolean;
    /** Sätze im Hinweis des Abschlussschritts (Sperre, offene Bestandteile, Lesefehler …). */
    abschlussHinweise: number;
    stundenOffen: boolean;
    /** Ein Abrechnungsschritt ist offen oder ein weiterer Plot dieses Projekts wartet. */
    weitererSchritt: boolean;
}): NachPlotSchliessen => {
    if (a.ablageGescheitert || a.hinweisTon === 'fehler' || a.hinweisTon === 'warnung' || a.abschlussHinweise > 0) return 'bleiben';
    if (a.ablageLaeuft || a.abschlussLaeuft || a.stundenOffen || a.weitererSchritt) return 'warten';
    return 'schliessen';
};

/** Buchungstext im Leistungsprotokoll, ohne Mengenangabe. */
export const abrechnungsBuchungsText = (
    ziel: AbrechnungsZiel['art'],
    planNr: string | null | undefined,
    status: string | null | undefined,
): string => `SiGe-Plan ${ziel === 'plot' ? 'geplottet' : 'exportiert'} (Plan-Nr. ${planNr || 'Entwurf'}, ${status ?? ''})`;
